import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type {
  ExtensionType, CallPlan, Feature, TenantTrunk, TenantTrunkStatus, Trunk, TrunkHistory,
} from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export const productKeys = {
  features: () => ['features'] as const,
  extensionTypes: () => ['extension-types'] as const,
  callPlans: () => ['call-plans'] as const,
  tenantTrunks: (tenantId: string) => ['tenant-trunks', tenantId] as const,
};

export const useFeatures = () =>
  useQuery({
    queryKey: productKeys.features(),
    queryFn: () => api.get<ListResponse<Feature>>('/v1/features'),
    // The feature catalogue changes on deploys, not on user actions.
    staleTime: 10 * 60_000,
  });

export const useExtensionTypes = () =>
  useQuery({
    queryKey: productKeys.extensionTypes(),
    queryFn: () => api.get<ListResponse<ExtensionType>>('/v1/extension-types'),
  });

export function useSaveExtensionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/extension-types/${id}`, body)
         : api.post('/v1/extension-types', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.extensionTypes() }); },
  });
}

export function useDeleteExtensionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/extension-types/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.extensionTypes() }); },
  });
}

export const useCallPlans = () =>
  useQuery({
    queryKey: productKeys.callPlans(),
    queryFn: () => api.get<ListResponse<CallPlan>>('/v1/call-plans'),
  });

export function useSaveCallPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/v1/call-plans', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.callPlans() }); },
  });
}

export const useTenantTrunks = (tenantId: string | null) =>
  useQuery({
    queryKey: productKeys.tenantTrunks(tenantId ?? ''),
    queryFn: () => api.get<ListResponse<TenantTrunk>>(`/v1/tenants/${tenantId}/trunks`),
    enabled: !!tenantId,
    // Live health and registration ride along (see useTrunks). Not after an
    // error: a customer is refused this staff view, and asking again every 15 s
    // would only be refused again.
    refetchInterval: (q) => (q.state.status === 'error' ? false : 15_000),
  });

/**
 * The customer-safe view of the same links: carrier, health, registration.
 * Held by customers as well as staff (connectivity:read), so this -- not
 * useTenantTrunks -- is what anything a customer sees must use.
 */
export const useTrunkStatus = (tenantId: string | null) =>
  useQuery({
    queryKey: ['trunk-status', tenantId ?? ''] as const,
    queryFn: () => api.get<ListResponse<TenantTrunkStatus>>(`/v1/tenants/${tenantId}/trunk-status`),
    enabled: !!tenantId,
    refetchInterval: (q) => (q.state.status === 'error' ? false : 15_000),
  });

/**
 * A customer's own trunks (bring your own trunk), and the account's mode. Read
 * by customers and staff alike (connectivity:read).
 */
export const useOwnTrunks = (tenantId: string | null) =>
  useQuery({
    queryKey: ['own-trunks', tenantId ?? ''] as const,
    queryFn: () => api.get<{ trunk_mode: 'arcnet' | 'byot'; data: Trunk[] }>(`/v1/tenants/${tenantId}/own-trunks`),
    enabled: !!tenantId,
    refetchInterval: (q) => (q.state.status === 'error' ? false : 15_000),
  });

/**
 * A trunk's record over the last `hours`: staff read any trunk; a customer only
 * one its calls use, through its own account.
 */
export const useTrunkHistory = (
  src: { trunkId: string; tenantId?: string | undefined; staff: boolean }, hours: number,
) =>
  useQuery({
    queryKey: ['trunk-history', src.trunkId, src.staff ? 'staff' : src.tenantId, hours] as const,
    queryFn: () => api.get<TrunkHistory>(src.staff
      ? `/v1/trunks/${src.trunkId}/history?hours=${hours}`
      : `/v1/tenants/${src.tenantId}/trunks/${src.trunkId}/history?hours=${hours}`),
    enabled: !!src.trunkId && (src.staff || !!src.tenantId),
    // One sample per 30 s: a minute is as fresh as the record can usefully get.
    refetchInterval: (q) => (q.state.status === 'error' ? false : 60_000),
  });

/** Every view of an account's trunks, refreshed together after a change to any. */
export function invalidateAccountTrunks(qc: ReturnType<typeof useQueryClient>, tenantId: string): void {
  // ['tenant'] whole: an account page may have loaded it by slug, not by id.
  for (const key of [['own-trunks', tenantId], ['trunk-status', tenantId], productKeys.tenantTrunks(tenantId), ['tenant'], ['tenants']]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

/** Our trunks or the customer's own: the provider's switch (trunk:write). */
export function useSetTrunkMode(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trunk_mode: 'arcnet' | 'byot') =>
      api.put<{ trunk_mode: string }>(`/v1/tenants/${tenantId}/trunk-mode`, { trunk_mode }),
    onSuccess: () => invalidateAccountTrunks(qc, tenantId),
  });
}

export function useDeleteOwnTrunk(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/own-trunks/${id}`),
    onSuccess: () => invalidateAccountTrunks(qc, tenantId),
  });
}

export function useLinkTrunk(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/v1/tenants/${tenantId}/trunks`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.tenantTrunks(tenantId) }); },
  });
}

export function useUnlinkTrunk(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/trunks/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.tenantTrunks(tenantId) }); },
  });
}

export interface TypeFeature {
  key: string; name: string; description: string;
  category: string; unit_cost?: string; from_type: boolean;
}

/**
 * What an extension type brings with it, read BEFORE a seat exists.
 *
 * So the create form can say what picking a type will do at the moment it is
 * picked, rather than after saving. Keyed on the type, so switching the picker
 * swaps to a cached answer instead of refetching one already seen.
 */
export const useTypeFeatures = (typeId: string | null) =>
  useQuery({
    queryKey: ['extension-types', typeId, 'features'] as const,
    queryFn: () => api.get<{ data: TypeFeature[] }>(`/v1/extension-types/${typeId}/features`),
    enabled: !!typeId,
    staleTime: 5 * 60_000,
  });
